'use client';

/**
 * ChatPanel - Schema-driven chat view
 *
 * Renders chat sections based on CHAT_SCHEMA configuration.
 * Replaces entity-specific chat components with a single, schema-driven component.
 *
 * Layout: Messages section scrolls, send sections pinned at bottom.
 *
 * @component_role Chat View
 * @business_rule Renders sections based on schema config for any entity type
 * @integration_point Uses CHAT_SCHEMA from total-matrix store
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useChatSchema } from '@/lib/stores/total-matrix-store';
import { ChatSectionRenderer, type ChatPanelData } from './chat-section-renderer';
import { LoadingState } from './shared';

// Section types that should scroll (take remaining space)
const SCROLLABLE_SECTIONS = new Set(['messages']);

// Section types that should be pinned (fixed height at bottom)
const PINNED_SECTIONS = new Set(['whatsapp_send', 'chatter_post', 'messenger_tabs']);

interface ChatPanelProps {
  entityType: string;
  entityId: string;
  data: ChatPanelData;
}

export function ChatPanel({ entityType, entityId, data }: ChatPanelProps) {
  const schema = useChatSchema(entityType);

  // Schema not loaded yet
  if (!schema) {
    return <LoadingState message="Loading chat schema..." />;
  }

  // Separate sections into scrollable (messages) and pinned (send actions)
  const scrollableSections = schema.sections.filter((s) => SCROLLABLE_SECTIONS.has(s.type));
  const pinnedSections = schema.sections.filter((s) => PINNED_SECTIONS.has(s.type));

  // Debug: log what sections we're rendering
  console.log('[ChatPanel] entityType:', entityType);
  console.log('[ChatPanel] Full schema:', JSON.stringify(schema, null, 2));
  console.log('[ChatPanel] Schema sections:', schema.sections.map(s => s.type));
  console.log('[ChatPanel] Scrollable sections:', scrollableSections.map(s => s.type));
  console.log('[ChatPanel] Pinned sections:', pinnedSections.map(s => s.type));
  console.log('[ChatPanel] Has onPostToChatter:', !!data.onPostToChatter);
  console.log('[ChatPanel] data keys:', Object.keys(data));

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable area - messages take remaining space */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {scrollableSections.map((section, idx) => (
          <ChatSectionRenderer key={`${section.type}-${idx}`} config={section} data={data} />
        ))}
      </div>

      {/* Pinned area - send sections always visible at bottom */}
      <div className="shrink-0 border-t border-panel-border">
        {pinnedSections.map((section, idx) => {
          console.log(`[ChatPanel] Rendering pinned section ${idx}: ${section.type}`);
          return (
            <ChatSectionRenderer key={`${section.type}-${idx}`} config={section} data={data} />
          );
        })}
      </div>
    </div>
  );
}
